import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ApprovedUser, UserFormData, UserRole } from "@/types";
import { Office } from "@/types/office";
import { getActiveOffices } from "@/services/officeService";
import { getAllActiveUsers } from "@/services/userService";
import { Loader2, User, Building2, Phone as PhoneIcon, Shield } from "lucide-react";
import { toast } from "sonner";
import { useRoleOptions } from "@/hooks/useRoles";
import { format } from "date-fns";
import { normalizePhoneNumber } from "@/lib/constants";

interface UserFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: ApprovedUser | null;
  onSave: (formData: UserFormData) => Promise<void>;
}

const initialFormData: UserFormData = {
  name: '',
  phone: '',
  email: '',
  officeId: '',
  roles: [],
  primaryRole: 'app_user',
  employeeId: '',
  designation: '',
  department: '',
  joiningDate: '',
  reportingTo: '',
  emergencyContact: {
    name: '',
    relationship: '',
    phone: '',
  },
};

export function UserFormDialog({ open, onOpenChange, user, onSave }: UserFormDialogProps) {
  const [formData, setFormData] = useState<UserFormData>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [offices, setOffices] = useState<Office[]>([]);
  const [users, setUsers] = useState<ApprovedUser[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Fetch available roles from Firebase
  const { options: roleOptions, loading: rolesLoading } = useRoleOptions();

  // Load offices and users for reporting manager dropdown
  useEffect(() => {
    if (open) {
      loadInitialData();
    }
  }, [open]);

  // Populate form when editing
  useEffect(() => {
    if (user && open) {
      setFormData({
        name: user.name,
        phone: user.phone,
        email: user.email || '',
        officeId: user.officeId,
        roles: user.roles,
        primaryRole: user.primaryRole,
        employeeId: user.employeeId || '',
        designation: user.designation || '',
        department: user.department || '',
        joiningDate: user.joiningDate ? (typeof user.joiningDate === 'string' ? user.joiningDate : format(user.joiningDate, 'yyyy-MM-dd')) : '',
        reportingTo: user.reportingTo || 'none',
        emergencyContact: user.emergencyContact || {
          name: '',
          relationship: '',
          phone: '',
        },
      });
    } else if (!user && open) {
      setFormData(initialFormData);
    }
  }, [user, open]);

  const loadInitialData = async () => {
    setLoadingData(true);
    try {
      const [officesData, usersData] = await Promise.all([
        getActiveOffices(),
        getAllActiveUsers(),
      ]);
      setOffices(officesData);
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load offices and users');
    } finally {
      setLoadingData(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^(\+91)?\d{10}$/.test(formData.phone)) {
      newErrors.phone = 'Phone number must be 10 digits or +91 followed by 10 digits';
    }

    if (!formData.officeId) {
      newErrors.officeId = 'Office selection is required';
    }

    if (formData.roles.length === 0) {
      newErrors.roles = 'At least one role must be selected';
    }

    if (!formData.primaryRole) {
      newErrors.primaryRole = 'Primary role is required';
    } else if (!formData.roles.includes(formData.primaryRole)) {
      newErrors.primaryRole = 'Primary role must be one of the selected roles';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix all validation errors');
      return;
    }

    setLoading(true);
    try {
      // Normalize phone before saving
      const normalizedFormData = {
        ...formData,
        phone: normalizePhoneNumber(formData.phone),
      };
      
      await onSave(normalizedFormData);
      onOpenChange(false);
      setFormData(initialFormData);
      setErrors({});
    } catch (error) {
      console.error('Error saving user:', error);
      // Error toast is already shown in the service
    } finally {
      setLoading(false);
    }
  };

  const handleRoleToggle = (role: UserRole, checked: boolean) => {
    let newRoles: UserRole[];
    
    if (checked) {
      newRoles = [...formData.roles, role];
    } else {
      newRoles = formData.roles.filter((r) => r !== role);
      
      // If removing primary role, clear it
      if (formData.primaryRole === role) {
        setFormData((prev) => ({ ...prev, primaryRole: newRoles[0] || 'app_user' }));
      }
    }

    setFormData((prev) => ({ ...prev, roles: newRoles }));
  };

  const handleInputChange = (field: keyof UserFormData, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleEmergencyContactChange = (field: keyof UserFormData['emergencyContact'], value: string) => {
    setFormData((prev) => ({
      ...prev,
      emergencyContact: {
        ...prev.emergencyContact,
        [field]: value,
      },
    }));
  };

  // Filter out current user from reporting manager list
  const availableManagers = users.filter((u) => u.id !== user?.id);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const headerContent = (
    <>
      <User className="h-5 w-5" />
      {user ? 'Edit User' : 'Add New User'}
    </>
  );
  const headerDescription = user ? 'Update user information and permissions' : 'Create a new user account with role assignments';

  return (
    <>
      {/* Mobile: Bottom Sheet */}
      <Sheet open={open && isMobile} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="h-[90vh] rounded-t-3xl flex flex-col p-0 overflow-hidden">
          <SheetHeader className="px-6 pt-6 pb-4 border-b flex-shrink-0">
            <SheetTitle className="text-xl font-semibold flex items-center gap-2">
              {headerContent}
            </SheetTitle>
            <SheetDescription className="text-sm text-muted-foreground">{headerDescription}</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 pb-6">
        {loadingData ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <form id="user-form" onSubmit={handleSubmit} className="space-y-6">
            {/* Section 1: Basic Information */}
            <div className="space-y-4 pt-4">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-green-600" />
                <span className="font-semibold text-green-800">Basic Information</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">
                    Full Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter full name"
                    className={errors.name ? 'border-destructive' : ''}
                  />
                  {errors.name && (
                    <p className="text-xs text-destructive">{errors.name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">
                    Phone Number <span className="text-destructive">*</span>
                  </Label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-sm text-muted-foreground">+91</span>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone.replace(/^\+91/, '')}
                      onChange={(e) => handleInputChange('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder="10-digit mobile number"
                      maxLength={10}
                      disabled={!!user}
                      className={`rounded-l-none ${errors.phone ? 'border-destructive' : ''}`}
                    />
                  </div>
                  {errors.phone && (
                    <p className="text-xs text-destructive">{errors.phone}</p>
                  )}
                  {user && (
                    <p className="text-xs text-muted-foreground">Phone number cannot be changed</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="user@example.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="office">
                    Office <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={formData.officeId}
                    onValueChange={(value) => handleInputChange('officeId', value)}
                  >
                    <SelectTrigger className={errors.officeId ? 'border-destructive' : ''}>
                      <SelectValue placeholder="Select office" />
                    </SelectTrigger>
                    <SelectContent>
                      {offices.map((office) => (
                        <SelectItem key={office.id} value={office.id}>
                          <div className="flex items-center gap-2">
                            <Building2 className="h-3 w-3" />
                            {office.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.officeId && (
                    <p className="text-xs text-destructive">{errors.officeId}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Section 2: Roles & Permissions */}
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-green-600" />
                <span className="font-semibold text-green-800">Roles & Permissions</span>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>
                    Assigned Roles <span className="text-destructive">*</span>
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {roleOptions.map((role) => (
                      <div key={role.value} className="flex items-center space-x-2">
                        <Checkbox
                          id={role.value}
                          checked={formData.roles.includes(role.value)}
                          onCheckedChange={(checked) => handleRoleToggle(role.value, checked as boolean)}
                        />
                        <label
                          htmlFor={role.value}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                          title={role.description}
                        >
                          {role.label}
                        </label>
                      </div>
                    ))}
                  </div>
                  {errors.roles && (
                    <p className="text-xs text-destructive">{errors.roles}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="primaryRole">
                    Primary Role <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={formData.primaryRole}
                    onValueChange={(value) => handleInputChange('primaryRole', value as UserRole)}
                    disabled={formData.roles.length === 0}
                  >
                    <SelectTrigger className={errors.primaryRole ? 'border-destructive' : ''}>
                      <SelectValue placeholder="Select primary role" />
                    </SelectTrigger>
                    <SelectContent>
                      {formData.roles.map((role) => {
                        const roleLabel = roleOptions.find((r) => r.value === role)?.label || role;
                        return (
                          <SelectItem key={role} value={role}>
                            {roleLabel}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {errors.primaryRole && (
                    <p className="text-xs text-destructive">{errors.primaryRole}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Primary role determines the default dashboard view
                  </p>
                </div>
              </div>
            </div>

            {/* Section 3: Employee Details */}
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-green-600" />
                <span className="font-semibold text-green-800">Employee Details</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="employeeId">Employee ID</Label>
                  <Input
                    id="employeeId"
                    value={formData.employeeId}
                    onChange={(e) => handleInputChange('employeeId', e.target.value)}
                    placeholder="EMP001"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="designation">Designation</Label>
                  <Input
                    id="designation"
                    value={formData.designation}
                    onChange={(e) => handleInputChange('designation', e.target.value)}
                    placeholder="Sales Manager"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="department">Department</Label>
                  <Input
                    id="department"
                    value={formData.department}
                    onChange={(e) => handleInputChange('department', e.target.value)}
                    placeholder="Sales"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="joiningDate">Joining Date</Label>
                  <Input
                    id="joiningDate"
                    type="date"
                    value={formData.joiningDate}
                    onChange={(e) => handleInputChange('joiningDate', e.target.value)}
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="reportingTo">Reporting Manager</Label>
                  <Select
                    value={formData.reportingTo || 'none'}
                    onValueChange={(value) => handleInputChange('reportingTo', value === 'none' ? '' : value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select reporting manager" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Manager</SelectItem>
                      {availableManagers.map((manager) => (
                        <SelectItem key={manager.id} value={manager.id}>
                          {manager.name} ({manager.designation || 'No Designation'})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Section 4: Emergency Contact */}
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center gap-2">
                <PhoneIcon className="h-4 w-4 text-green-600" />
                <span className="font-semibold text-green-800">Emergency Contact</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="emergencyName">Contact Name</Label>
                  <Input
                    id="emergencyName"
                    value={formData.emergencyContact.name}
                    onChange={(e) => handleEmergencyContactChange('name', e.target.value)}
                    placeholder="Emergency contact name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="emergencyRelationship">Relationship</Label>
                  <Input
                    id="emergencyRelationship"
                    value={formData.emergencyContact.relationship}
                    onChange={(e) => handleEmergencyContactChange('relationship', e.target.value)}
                    placeholder="Spouse, Parent, Sibling"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="emergencyPhone">Contact Phone</Label>
                  <Input
                    id="emergencyPhone"
                    type="tel"
                    value={formData.emergencyContact.phone}
                    onChange={(e) => handleEmergencyContactChange('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="10-digit phone number"
                    maxLength={10}
                  />
                </div>
              </div>
            </div>

          </form>
        )}
        </div>

        {!loadingData && (
          <div className="border-t px-6 py-4 shrink-0">
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="user-form"
                disabled={loading}
                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white"
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {user ? 'Update User' : 'Create User'}
              </Button>
            </div>
          </div>
        )}
        </SheetContent>
      </Sheet>

      {/* Desktop: Dialog */}
      <Dialog open={open && !isMobile} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] p-0 flex flex-col gap-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <DialogTitle className="text-xl font-semibold flex items-center gap-2">{headerContent}</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">{headerDescription}</DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto px-6 pb-6">
          {loadingData ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <form id="user-form-desktop" onSubmit={handleSubmit} className="space-y-6">
              {/* same sections as above — rendered inside dialog */}
              {/* Section 1 */}
              <div className="space-y-4 pt-4">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-green-600" />
                  <span className="font-semibold text-green-800">Basic Information</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name-d">Full Name <span className="text-destructive">*</span></Label>
                    <Input id="name-d" value={formData.name} onChange={(e) => handleInputChange('name', e.target.value)} placeholder="Enter full name" className={errors.name ? 'border-destructive' : ''} />
                    {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone-d">Phone Number <span className="text-destructive">*</span></Label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-sm text-muted-foreground">+91</span>
                      <Input id="phone-d" type="tel" value={formData.phone.replace(/^\+91/, '')} onChange={(e) => handleInputChange('phone', e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="10-digit mobile number" maxLength={10} disabled={!!user} className={`rounded-l-none ${errors.phone ? 'border-destructive' : ''}`} />
                    </div>
                    {errors.phone && <p className="text-xs text-destructive">{errors.phone}</p>}
                    {user && <p className="text-xs text-muted-foreground">Phone number cannot be changed</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email-d">Email Address</Label>
                    <Input id="email-d" type="email" value={formData.email} onChange={(e) => handleInputChange('email', e.target.value)} placeholder="user@example.com" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="office-d">Office <span className="text-destructive">*</span></Label>
                    <Select value={formData.officeId} onValueChange={(v) => handleInputChange('officeId', v)}>
                      <SelectTrigger className={errors.officeId ? 'border-destructive' : ''}><SelectValue placeholder="Select office" /></SelectTrigger>
                      <SelectContent>{offices.map((office) => (<SelectItem key={office.id} value={office.id}><div className="flex items-center gap-2"><Building2 className="h-3 w-3" />{office.name}</div></SelectItem>))}</SelectContent>
                    </Select>
                    {errors.officeId && <p className="text-xs text-destructive">{errors.officeId}</p>}
                  </div>
                </div>
              </div>
              {/* Section 2 */}
              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center gap-2"><Shield className="h-4 w-4 text-green-600" /><span className="font-semibold text-green-800">Roles & Permissions</span></div>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Assigned Roles <span className="text-destructive">*</span></Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {roleOptions.map((role) => (<div key={role.value} className="flex items-center space-x-2"><Checkbox id={`${role.value}-d`} checked={formData.roles.includes(role.value)} onCheckedChange={(c) => handleRoleToggle(role.value, c as boolean)} /><label htmlFor={`${role.value}-d`} className="text-sm font-medium leading-none cursor-pointer" title={role.description}>{role.label}</label></div>))}
                    </div>
                    {errors.roles && <p className="text-xs text-destructive">{errors.roles}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="primaryRole-d">Primary Role <span className="text-destructive">*</span></Label>
                    <Select value={formData.primaryRole} onValueChange={(v) => handleInputChange('primaryRole', v as UserRole)} disabled={formData.roles.length === 0}>
                      <SelectTrigger className={errors.primaryRole ? 'border-destructive' : ''}><SelectValue placeholder="Select primary role" /></SelectTrigger>
                      <SelectContent>{formData.roles.map((role) => { const lbl = roleOptions.find((r) => r.value === role)?.label || role; return (<SelectItem key={role} value={role}>{lbl}</SelectItem>); })}</SelectContent>
                    </Select>
                    {errors.primaryRole && <p className="text-xs text-destructive">{errors.primaryRole}</p>}
                    <p className="text-xs text-muted-foreground">Primary role determines the default dashboard view</p>
                  </div>
                </div>
              </div>
              {/* Section 3 */}
              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-green-600" /><span className="font-semibold text-green-800">Employee Details</span></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label htmlFor="empId-d">Employee ID</Label><Input id="empId-d" value={formData.employeeId} onChange={(e) => handleInputChange('employeeId', e.target.value)} placeholder="EMP001" /></div>
                  <div className="space-y-2"><Label htmlFor="desig-d">Designation</Label><Input id="desig-d" value={formData.designation} onChange={(e) => handleInputChange('designation', e.target.value)} placeholder="Sales Manager" /></div>
                  <div className="space-y-2"><Label htmlFor="dept-d">Department</Label><Input id="dept-d" value={formData.department} onChange={(e) => handleInputChange('department', e.target.value)} placeholder="Sales" /></div>
                  <div className="space-y-2"><Label htmlFor="join-d">Joining Date</Label><Input id="join-d" type="date" value={formData.joiningDate} onChange={(e) => handleInputChange('joiningDate', e.target.value)} /></div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="mgr-d">Reporting Manager</Label>
                    <Select value={formData.reportingTo || 'none'} onValueChange={(v) => handleInputChange('reportingTo', v === 'none' ? '' : v)}>
                      <SelectTrigger><SelectValue placeholder="Select reporting manager" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">No Manager</SelectItem>{availableManagers.map((m) => (<SelectItem key={m.id} value={m.id}>{m.name} ({m.designation || 'No Designation'})</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              {/* Section 4 */}
              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center gap-2"><PhoneIcon className="h-4 w-4 text-green-600" /><span className="font-semibold text-green-800">Emergency Contact</span></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label htmlFor="ecName-d">Contact Name</Label><Input id="ecName-d" value={formData.emergencyContact.name} onChange={(e) => handleEmergencyContactChange('name', e.target.value)} placeholder="Emergency contact name" /></div>
                  <div className="space-y-2"><Label htmlFor="ecRel-d">Relationship</Label><Input id="ecRel-d" value={formData.emergencyContact.relationship} onChange={(e) => handleEmergencyContactChange('relationship', e.target.value)} placeholder="Spouse, Parent, Sibling" /></div>
                  <div className="space-y-2 md:col-span-2"><Label htmlFor="ecPhone-d">Contact Phone</Label><Input id="ecPhone-d" type="tel" value={formData.emergencyContact.phone} onChange={(e) => handleEmergencyContactChange('phone', e.target.value.replace(/\D/g,'').slice(0,10))} placeholder="10-digit phone number" maxLength={10} /></div>
                </div>
              </div>
            </form>
          )}
          </div>
          {!loadingData && (
            <div className="border-t px-6 py-4 shrink-0">
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>Cancel</Button>
                <Button type="submit" form="user-form-desktop" disabled={loading} className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white">
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {user ? 'Update User' : 'Create User'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
